import { useEffect, useMemo, useState, Dispatch, SetStateAction, ChangeEvent } from 'react';

import { map } from 'ramda';
import cloneDeep from 'lodash/cloneDeep';
import isNil from 'lodash/isNil';
import isEmpty from 'lodash/isEmpty';

import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import RadioGroup from '@material-ui/core/RadioGroup';
import Radio from '@material-ui/core/Radio';
import DetailedInputLabel from '../../../../inputfields/InputLabel';
import InputLabel from '../../../../inputfields/InputLabel';
import ListDropDown from '../../../../inputfields/ListDropDown';
import { PRODUCT_CLASSIFICATIONS, getPercentageValues, RECYCLING_AMOUNT } from '../../../../constants';
import ThreeWaySwitch from '../../../../inputfields/ThreeWaySwitch';
import IProduct, { IFoodlaCategory, IFullFoodlaCategoryTree } from '../../../../../models/product';
import { IFoodlaCategoryOption } from 'models/category';

import { eggWeightClass, recyclingType } from '../../../../constants-ts';
import CategoryTree, { findCategory } from './category-tree';
import OrgCategory from './org-category';
import useCategoriesData from 'hooks/useCategoriesData';
import { OrgCategoryDisplay } from './org-category-display';
import { categoryTypeToStateField } from './helpers';
import { IImage } from 'components/images/ImageSorter';
import { restoreApprove, syncCurrentApprove } from '../../utils-ts';
import { useIsFieldRequired } from '../../validation-context/ProductValidationContext';
import MissingFieldHint from '../../validation-context/MissingFieldHint';
import type { ProductFieldVisibility } from '../../utils/productFieldVisibility';

export { getParentCategoriesData } from './helpers';

const isCategoryOverridden = (state: IProduct, type: 'ica' | 'coop' | 'apotea') => {
  const stateField = categoryTypeToStateField[type];
  const result = Boolean(state[stateField]);
  return result;
};

export const isCategoryDimmed = (state: IProduct, type: 'ica' | 'coop' | 'wolt' | 'apotea') => {
  if (type === 'ica') {
    const enabled = !isNil(state.producerOrganizations) && state.producerOrganizations.includes('ica');
    const dimmed = !enabled;
    return dimmed;
  } else if (type === 'coop') {
    const enabled = isNil(state.producerOrganizations) || state.producerOrganizations.includes('coop');
    const dimmed = !enabled;
    return dimmed;
  } else if (type === 'wolt') {
    const enabled = !isNil(state.producerOrganizations) && state.producerOrganizations.includes('wolt');
    const dimmed = !enabled;
    return dimmed;
  } else if (type === 'apotea') {
    const enabled = !isNil(state.producerOrganizations) && state.producerOrganizations.includes('apotea');
    const dimmed = !enabled;
    return dimmed;
  }
};

const VSpace = ({ h }: { h: string }) => <div style={{ height: h }} />;

interface ICategoryProps {
  readOnly?: boolean;
  sortedImages: IImage[];
  isUserStore: boolean;
  isSuperuser?: boolean;
  product?: IProduct;
  state: IProduct;
  setState: Dispatch<SetStateAction<IProduct>>;
  approve?: boolean;
  userRole?: string;
  rootCategory?: IFoodlaCategoryOption;
  categories: IFoodlaCategoryOption[];
  categoryLoading?: boolean;
  categoryError?: string;
  fieldVisibility: ProductFieldVisibility['fields'];
}

export const getFullCategoryTree = (category: IFoodlaCategory, rootCategory: IFoodlaCategoryOption) => {
  let fullCategoryTree: IFullFoodlaCategoryTree = {
    id: category.id,
    name: category.name,
  };

  const findParent = (currentCategory: IFoodlaCategory, rootCategory: IFoodlaCategoryOption) => {
    if (rootCategory.children) {
      for (const child of rootCategory.children) {
        if (child.id === currentCategory.id) {
          return true;
        }
        if (findParent(currentCategory, child)) {
          fullCategoryTree = {
            id: child.id,
            name: child.name,
            child: fullCategoryTree,
          };
          return true;
        }
      }
    }
    return null;
  };
  findParent(category, rootCategory);

  return fullCategoryTree;
};

export const getParentCategories = (category: IFoodlaCategory, rootCategory: IFoodlaCategoryOption) => {
  const parent: string[] = [];
  const findParent = (currentCategory: IFoodlaCategory, rootCategory: IFoodlaCategoryOption) => {
    if (rootCategory.children) {
      for (const child of rootCategory.children) {
        if (child.id === currentCategory.id) {
          parent.push(child.name);
          return true;
        }
        if (findParent(currentCategory, child)) {
          parent.push(child.name);
          return true;
        }
      }
    }
    return false;
  };
  findParent(category, rootCategory);
  return parent;
};

const isCategory = (category: string, currentCategory?: IFoodlaCategory, rootCategory?: IFoodlaCategoryOption) => {
  if (!currentCategory || !rootCategory) return false;
  const parent = getParentCategories(currentCategory, rootCategory);

  return parent.includes(category) || currentCategory.name === category;
};

export const isEgg = (category?: IFoodlaCategory, rootCategory?: IFoodlaCategoryOption) => {
  return isCategory('Ägg och äggprodukter', category, rootCategory);
};

export const isFruktOrGreen = (category?: IFoodlaCategory, rootCategory?: IFoodlaCategoryOption) => {
  return isCategory('Frukter, bär och grönsaker', category, rootCategory);
};

export const isBeverages = (category?: IFoodlaCategory, rootCategory?: IFoodlaCategoryOption) => {
  return isCategory('Drycker', category, rootCategory);
};

export const isFish = (category?: IFoodlaCategory, rootCategory?: IFoodlaCategoryOption, state?: IProduct) => {
  return (
    isCategory('Fisk och fiskeriprodukter', category, rootCategory) ||
    (state?.producerIsStore && isCategory('Egen fiskdisk', category, rootCategory))
  );
};

export const isFoodSupplements = (category?: IFoodlaCategory, rootCategory?: IFoodlaCategoryOption) => {
  return isCategory('Kosttillskott enligt definitionen i direktiv 2002/46/EG', category, rootCategory);
};

export const isNewFood = (category?: IFoodlaCategory, rootCategory?: IFoodlaCategoryOption) => {
  return isCategory('Food', category, rootCategory);
};

export const isNewNonFood = (category?: IFoodlaCategory, rootCategory?: IFoodlaCategoryOption) => {
  return isCategory('Non food', category, rootCategory);
};

export const isToy = (category?: IFoodlaCategory, rootCategory?: IFoodlaCategoryOption) => {
  return isCategory('Leksak', category, rootCategory);
};

export const isElectric = (category?: IFoodlaCategory, rootCategory?: IFoodlaCategoryOption) => {
  return isCategory('Elektrisk', category, rootCategory);
};

export const isJewelry = (category?: IFoodlaCategory, rootCategory?: IFoodlaCategoryOption) => {
  return isCategory('Smycken, kläder, glasögon, accessoarer', category, rootCategory);
};

export const isGoods = (category?: IFoodlaCategory, rootCategory?: IFoodlaCategoryOption) => {
  return isCategory('Varor', category, rootCategory);
};

export const isSewingKnittingYarn = (category?: IFoodlaCategory, rootCategory?: IFoodlaCategoryOption) => {
  return isCategory('Sy & sticka garn', category, rootCategory);
};

export const isMedical = (category?: IFoodlaCategory, rootCategory?: IFoodlaCategoryOption) => {
  return isCategory('Medicinteknik', category, rootCategory);
};

export const isChemical = (category?: IFoodlaCategory, rootCategory?: IFoodlaCategoryOption) => {
  return isCategory('Kemiska produkter', category, rootCategory);
};

export const isDetergent = (category?: IFoodlaCategory, rootCategory?: IFoodlaCategoryOption) => {
  return isCategory('Tvättmedel/Diskmedel', category, rootCategory);
};

export const isCosmetic = (category?: IFoodlaCategory, rootCategory?: IFoodlaCategoryOption) => {
  return isCategory('Kosmetiska produkter', category, rootCategory);
};

export const isComplementarySupplements = (category?: IFoodlaCategory, rootCategory?: IFoodlaCategoryOption) => {
  return isCategory('Kompletteringsfoder & Fodertillskott', category, rootCategory);
};

export const isSunscreen = (category?: IFoodlaCategory, rootCategory?: IFoodlaCategoryOption) => {
  return isCategory('Solskyddsmedel', category, rootCategory);
};

export const isFeedMaterial = (category?: IFoodlaCategory, rootCategory?: IFoodlaCategoryOption) => {
  return isCategory('Foderråvara', category, rootCategory);
};

export const isFeedMixtures = (category?: IFoodlaCategory, rootCategory?: IFoodlaCategoryOption) => {
  return isCategory('Foderblandningar', category, rootCategory);
};

export const isCompleteFeed = (category?: IFoodlaCategory, rootCategory?: IFoodlaCategoryOption) => {
  return isCategory('Helfoder', category, rootCategory);
};

export const isOTC = (category?: IFoodlaCategory, rootCategory?: IFoodlaCategoryOption) => {
  return isCategory('Receptfria läkemedel', category, rootCategory);
};

export const isOtherFoodstuffs = (category?: IFoodlaCategory, rootCategory?: IFoodlaCategoryOption) => {
  return isCategory('Övrigt livsmedel', category, rootCategory);
};

export const isWhiteSnus = (category?: IFoodlaCategory, rootCategory?: IFoodlaCategoryOption) => {
  return isCategory('Vitt snus', category, rootCategory);
};

export const isElectricLamp = (category?: IFoodlaCategory, rootCategory?: IFoodlaCategoryOption) => {
  // "Lampor" also exist in "Varor". So check "Elektrisk" first.
  if (!isElectric(category, rootCategory)) return false;

  return (
    isCategory('Lysrörslampor/glödlampor', category, rootCategory) ||
    isCategory('Special & glödlampor', category, rootCategory) ||
    (isCategory('Lampor', category, rootCategory) &&
      !isCategory('Ficklampor & bärbara lampor', category, rootCategory) &&
      !isCategory('Lampor dekorationsbelysning', category, rootCategory) &&
      !isCategory('Led lampor dekorationsbelysning', category, rootCategory) &&
      !isCategory('Lampor säkringar & proppar', category, rootCategory))
  );
};

export const isSnusOrTobacco = (category?: IFoodlaCategory, rootCategory?: IFoodlaCategoryOption) => {
  return (
    (isCategory('Snus & tobak', category, rootCategory) && isNewFood(category, rootCategory)) ||
    (isCategory('Vitt snus', category, rootCategory) && isNewNonFood(category, rootCategory))
  );
};

export const isSpecialFoodNutrition = (category?: IFoodlaCategory, rootCategory?: IFoodlaCategoryOption) => {
  return isCategory('Livsmedel för särskilda näringsändamål', category, rootCategory);
};

export const isSpicesSoupsSalads = (category?: IFoodlaCategory, rootCategory?: IFoodlaCategoryOption) => {
  return isCategory('Salt, kryddor, soppor, såser, sallader och proteinprodukter', category, rootCategory);
};

export const isMeat = (category?: IFoodlaCategory, rootCategory?: IFoodlaCategoryOption) => {
  return isCategory('Kött', category, rootCategory);
};

export const isBakeryProducts = (category?: IFoodlaCategory, rootCategory?: IFoodlaCategoryOption) => {
  return isCategory('Bageriprodukter', category, rootCategory);
};

export const isMilk = (category?: IFoodlaCategory, rootCategory?: IFoodlaCategoryOption) => {
  return isCategory('Mjölkprodukter och motsvarande icke mjölkbaserade produkter', category, rootCategory);
};

export const isCereals = (category?: IFoodlaCategory, rootCategory?: IFoodlaCategoryOption) => {
  return isCategory('Spannmål och spannmålsprodukter', category, rootCategory);
};

export const isConfectionery = (category?: IFoodlaCategory, rootCategory?: IFoodlaCategoryOption) => {
  return isCategory('Konfektyrvaror', category, rootCategory);
};

export const isConsumptionReadySnacks = (category?: IFoodlaCategory, rootCategory?: IFoodlaCategoryOption) => {
  return isCategory('Konsumtionsfärdiga snacks', category, rootCategory);
};

export const isSweet = (category?: IFoodlaCategory, rootCategory?: IFoodlaCategoryOption) => {
  return isCategory('Sockerarter, olika former av sirap, honung och bordssötningsmedel', category, rootCategory);
};

export const isFats = (category?: IFoodlaCategory, rootCategory?: IFoodlaCategoryOption) => {
  return isCategory('Fetter och oljor samt fett- och oljeemulsioner', category, rootCategory);
};

export const isIceCream = (category?: IFoodlaCategory, rootCategory?: IFoodlaCategoryOption) => {
  return isCategory('Glassvaror', category, rootCategory);
};

export const isMagazine = (category?: IFoodlaCategory, rootCategory?: IFoodlaCategoryOption) => {
  return isCategory('Tidningar', category, rootCategory);
};

export const isBurningCandles = (category?: IFoodlaCategory, rootCategory?: IFoodlaCategoryOption) => {
  // not [Varor]
  if (!isGoods(category, rootCategory)) return false;
  // is [Varor → Uteljus]
  if (isCategory('Uteljus', category, rootCategory)) return true;
  // not [Varor → Ljus]
  if (!isCategory('Ljus', category, rootCategory)) return false;
  // is [Varor → Ljus → Ljustillbehör]
  if (isCategory('Ljustillbehör', category, rootCategory)) return false;

  return true;
};

export const isFeedCategory = (category?: IFoodlaCategory, rootCategory?: IFoodlaCategoryOption) => {
  return (
    isComplementarySupplements(category, rootCategory) ||
    isFeedMaterial(category, rootCategory) ||
    isFeedMixtures(category, rootCategory) ||
    isCompleteFeed(category, rootCategory)
  );
};

export const isCmrHazardCategory = (category?: IFoodlaCategory, rootCategory?: IFoodlaCategoryOption) => {
  return (
    isGoods(category, rootCategory) ||
    isJewelry(category, rootCategory) ||
    isToy(category, rootCategory) ||
    isElectric(category, rootCategory) ||
    isDetergent(category, rootCategory)
  );
};

export const isOtherFoodGroup = (category?: IFoodlaCategory, rootCategory?: IFoodlaCategoryOption) => {
  return (
    isBakeryProducts(category, rootCategory) ||
    isMilk(category, rootCategory) ||
    isCereals(category, rootCategory) ||
    isConfectionery(category, rootCategory) ||
    isConsumptionReadySnacks(category, rootCategory) ||
    isSweet(category, rootCategory) ||
    isFats(category, rootCategory) ||
    isIceCream(category, rootCategory)
  );
};

export const isPharma = (category?: IFoodlaCategory, rootCategory?: IFoodlaCategoryOption) => {
  return (
    isChemical(category, rootCategory) ||
    isMedical(category, rootCategory) ||
    isCosmetic(category, rootCategory) ||
    isSunscreen(category, rootCategory) ||
    isOTC(category, rootCategory)
  );
};

const isInCategory = (currentCategory?: IFoodlaCategory, category?: string, rootCategory?: IFoodlaCategoryOption) => {
  if (
    !currentCategory ||
    !!currentCategory.name ||
    currentCategory.name.trim() === '' ||
    !category ||
    category.trim() === '' ||
    !rootCategory
  ) {
    return false;
  }
  if (currentCategory.name === category) {
    return true;
  }
  if (rootCategory.children) {
    for (const child of rootCategory.children) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      if (isInCategory(child, category, currentCategory)) {
        return true;
      }
    }
  }
  return false;
};

export const isElectricBattery = (category?: IFoodlaCategory, rootCategory?: IFoodlaCategoryOption) => {
  return isCategory('Batterier', category, rootCategory);
};

type TApproveFieldConstant = {
  field: keyof IProduct | 'images' | 'nettokvantitet' | 'supplierArticleNo';
  changeFields?: string[];
  conditionFunc: (
    product: IProduct,
    rootCategory?: IFoodlaCategoryOption,
    option?: {
      isApoteaProducer?: boolean,
      isStoreProducer?: boolean,
      isMultiBrandProducer?: boolean
    },
  ) => boolean | undefined;
};

const APPROVE_FIELDS_ALL: TApproveFieldConstant[] = [
  {
    field: 'title',
    conditionFunc: () => true,
  },
  {
    field: 'foodlaCategory',
    conditionFunc: () => true,
  },
  {
    field: 'images',
    conditionFunc: () => true,
  },
  {
    field: 'EAN',
    conditionFunc: () => true,
  },
  {
    field: 'short_text',
    conditionFunc: () => true,
  },
  {
    field: 'labels',
    conditionFunc: () => true,
  },
  {
    field: 'nettokvantitet',
    changeFields: ['descriptive_size_amount', 'descriptive_size_unit'],
    conditionFunc: () => true,
  },
  {
    field: 'trade_item_temperature_information',
    conditionFunc: () => true,
  },
  {
    field: 'gross_weight_num',
    conditionFunc: product => !product?.producerOrg?.startsWith('coop/'),
  },
  {
    field: 'gross_height',
    changeFields: ['gross_height', 'gross_width', 'gross_depth'],
    conditionFunc: product => !product?.producerOrg?.startsWith('coop/'),
  },
  {
    field: 'search_terms',
    conditionFunc: () => true,
  },
  {
    field: 'wasteManagementAndRecyclingInformation',
    conditionFunc: () => true,
  },
  {
    field: "shortSalesDescription",
    conditionFunc: (product, rootCategory, option) => option?.isApoteaProducer,
  },
  {
    field: "color",
    conditionFunc: (product, rootCategory) =>
      !isWhiteSnus(product?.foodlaCategory, rootCategory) &&
      (isGoods(product?.foodlaCategory, rootCategory) ||
        isJewelry(product?.foodlaCategory, rootCategory) ||
        isToy(product?.foodlaCategory, rootCategory) ||
        isElectric(product?.foodlaCategory, rootCategory)),
  },
  {
    field: "material",
    conditionFunc: (product, rootCategory) =>
      !isWhiteSnus(product?.foodlaCategory, rootCategory) &&
      (isGoods(product?.foodlaCategory, rootCategory) ||
        isJewelry(product?.foodlaCategory, rootCategory) ||
        isToy(product?.foodlaCategory, rootCategory) ||
        isElectric(product?.foodlaCategory, rootCategory) ||
        isMedical(product?.foodlaCategory, rootCategory)),
  },
  {
    field: "dimensions",
    conditionFunc: (product, rootCategory) =>
      !isWhiteSnus(product?.foodlaCategory, rootCategory) &&
      (isGoods(product?.foodlaCategory, rootCategory) ||
        isJewelry(product?.foodlaCategory, rootCategory) ||
        isToy(product?.foodlaCategory, rootCategory) ||
        isElectric(product?.foodlaCategory, rootCategory) ||
        isMedical(product?.foodlaCategory, rootCategory)),
  },
  {
    field: "capacity",
    conditionFunc: (product, rootCategory) =>
      !isWhiteSnus(product?.foodlaCategory, rootCategory) &&
      (isGoods(product?.foodlaCategory, rootCategory) ||
        isJewelry(product?.foodlaCategory, rootCategory) ||
        isToy(product?.foodlaCategory, rootCategory) ||
        isElectric(product?.foodlaCategory, rootCategory) ||
        isMedical(product?.foodlaCategory, rootCategory)),
  },
  {
    field: 'basicDataTaxClassification',
    conditionFunc: () => true,
  },
  {
    field: "commodityCode",
    conditionFunc: (product, rootCategory, option) => option?.isApoteaProducer,
  },
  {
    field: 'salesType',
    conditionFunc: () => true,
  },
  {
    field: 'nameNordicDistributor',
    conditionFunc: () => true,
  },
  {
    field: 'preparationInstructions',
    conditionFunc: (product, rootCategory) =>
      isFish(product?.foodlaCategory, rootCategory, product) ||
      isSpecialFoodNutrition(product?.foodlaCategory, rootCategory) ||
      isBeverages(product?.foodlaCategory, rootCategory) ||
      isEgg(product?.foodlaCategory, rootCategory) ||
      isFruktOrGreen(product?.foodlaCategory, rootCategory) ||
      isOtherFoodstuffs(product?.foodlaCategory, rootCategory) ||
      isMeat(product?.foodlaCategory, rootCategory) ||
      isBakeryProducts(product?.foodlaCategory, rootCategory) ||
      isMilk(product?.foodlaCategory, rootCategory) ||
      isCereals(product?.foodlaCategory, rootCategory) ||
      isConfectionery(product?.foodlaCategory, rootCategory) ||
      isConsumptionReadySnacks(product?.foodlaCategory, rootCategory) ||
      isSweet(product?.foodlaCategory, rootCategory) ||
      isSpicesSoupsSalads(product?.foodlaCategory, rootCategory) ||
      isFats(product?.foodlaCategory, rootCategory) ||
      isIceCream(product?.foodlaCategory, rootCategory)
  },
  {
    field: 'categoryWeightloss',
    conditionFunc: (product, rootCategory) =>
      isSpecialFoodNutrition(product?.foodlaCategory, rootCategory)
  },
  {
    field: "location",
    conditionFunc: (product, rootCategory, option) =>
      !option?.isStoreProducer,
  },
  {
    field: "burningTime",
    conditionFunc: (product, rootCategory) =>
      isBurningCandles(product?.foodlaCategory, rootCategory)
  },
  {
    field: "temperatures" as keyof IProduct,
    changeFields: [
      "min_temperature",
      "max_temperature",
      "transportMinTemperature",
      "transportMaxTemperature",
    ],
    conditionFunc: () => true,
  },
  {
    field: "consumerContact" as keyof IProduct,
    changeFields: [
      "consumerContactCompanyName",
      "consumerContactPhone",
      "consumerContactEmail",
      "consumerContactWebsite",
      "consumerContactAddress",
    ],
    conditionFunc: (product, rootCategory, option) =>
      option?.isMultiBrandProducer
  },
  {
    field: "battery" as keyof IProduct,
    changeFields: [
      "batteryModel",
      "batteryType",
      "batteryTypeOther",
      "batteryVoltage",
      "batteryTechnology",
      "batteryCapacity",
      "batteryLife",
      "batteryWeight",
      "batteryRechargeable",
      "manufacturingLocation",
      "batteryHazardousMetals",
    ],
    conditionFunc: (product, rootCategory) =>
      isElectricBattery(product?.foodlaCategory, rootCategory)
  },
  {
    field: "yarnSpecs" as keyof IProduct,
    changeFields: [
      "yarnWeight",
      "yarnRunLength",
      "recommendedCrochetHook",
      "recommendedKnittingNeedles",
      "knittingGaugeStitches",
      "knittingGaugeRows",
      "crochetGaugeStitches",
      "crochetGaugeRows",
    ],
    conditionFunc: (product, rootCategory) =>
      isSewingKnittingYarn(product?.foodlaCategory, rootCategory)
  },
  {
    field: "textileCare" as keyof IProduct,
    changeFields: [
      "washingTemperature",
      "washingInstructions",
      "ironing",
      "tumbleDrying",
      "dryCleaning",
      "chlorineBleaching",
    ],
    conditionFunc: (product, rootCategory) =>
      isSewingKnittingYarn(product?.foodlaCategory, rootCategory)
  },
  // TODO: Temporarily disabled packagingLogistics approval because it groups scattered fields across different sections (dates, supplier article, logistics) under a single review key, causing synchronized UI changes that confuse superusers.
//   {
//     field: "packagingLogistics" as keyof IProduct,
//     changeFields: [
//       "consumer_size_to_order_size_ratio",
//       "basicDataAvailabilityDateFrom",
//       "articleFirstOrderDate",
//       "purchasingDataSupplierArticleNo",
//       "consumerPackOrderableUnit",
//       "palletOrderableUnit",
//       "storePackOrderableUnit",
//       "transportOrderableUnit",
//       "consumerPackDespatchUnit",
//       "palletDespatchUnit",
//       "storePackDespatchUnit",
//       "transportDespatchUnit",
//       "palletQtyOfBaseItem",
//       "palletQtyOfNextLowerItem",
//       "palletQtyOfStorePack",
//       "transportQtyOfBaseItem",
//       "transportQtyOfNextLowerItem",
//       "palletLayerQtyOfBaseItem",
//       "palletLayerQtyOfNextLowerItem",
//       "lavQuantity",
//     ],
//     conditionFunc: (product, rootCategory, option) =>
//       !option?.isStoreProducer
//   },
  {
    field: "apoteaPrices" as keyof IProduct,
    changeFields: [
      "apoteaListPrice",
      "apoteaPurchasePrice",
      "apoteaStorePrice",
      "apoteaStorePriceNok",
    ],
    conditionFunc: (product, rootCategory, option) =>
      option?.isApoteaProducer,
  },
  {
    field: "qualityReview" as keyof IProduct,
    changeFields: [
      "ceMarking",
      "specifyCEStandards",
      "plasticTrayPackaging",
      "shrinkWrapPackaging",
      "bestBeforeDate",
      "childrenUnder16",
      "containsBattery",
      "compliancePops",
      "containsBannedSubstancesPops",
      "cmrSubstance",
      "CMRSubstances",
      "reachSubstance",
      "qrRelevantFeedCertifications",
      "qrFoodHygiene",
      "qrElectronic",
      "qrElectronicCode",
      "qrClassificationLabelingPackaging",
      "qrLabelingSwedish",
      "qrMedicalClaims",
      "qrMarketingClaimsCosmetic",
      "qrAdditivesInFeed",
      "qrFeedLabeling",
      "qrMisleadingSalesTerms",
      "qrNutritionClaims",
      "qrNutritionClaimsCode",
      "qrHealthClaims",
      "qrHealthClaimsCode",
      "qrFoodLabeling",
      "qrEfsaUlValues",
      "compliancePpwrPackaging",
      "complianceReach19072006",
      "reachSubstancesAnnex19072006",
      "complianceProductSafety2004451",
      "hazardousSubstancesOver1",
      "informationHazardousSubstances",
      "complianceMedicalTechProducts",
      "checkUniqueIdentification",
      "explanationNoUDI",
      "complianceHaccp",
      "approvalDietFeed",
      "complianceEg12232009",
      "complianceSunscreenRegulation",
      "sunProtectionFactor",
      "complianceMetalsSkinContact",
      "confirmGuidanceSwedishElectronics",
      "complianceToyDirective",
      "complianceBiocid",
      "complianceBiocidalTreated",
      "regulatedSubstance",
    ],
    conditionFunc: (product, rootCategory, option) => {
      const showQualityFields = !!option?.isApoteaProducer;
      const isNotStore = !option?.isStoreProducer;
      const isFood = isNewFood(product?.foodlaCategory, rootCategory);
      const showQrRelevantFeedCertifications = isFeedCategory(product?.foodlaCategory, rootCategory);
      const showQrFoodHygiene = isNotStore && isNewFood(product?.foodlaCategory, rootCategory);
      const showQrElectronic = (isToy(product?.foodlaCategory, rootCategory) ||
        isElectric(product?.foodlaCategory, rootCategory) ||
        isMedical(product?.foodlaCategory, rootCategory));
      const showQrClassificationLabelingPackaging = (isToy(product?.foodlaCategory, rootCategory) ||
      isChemical(product?.foodlaCategory, rootCategory) ||
      isDetergent(product?.foodlaCategory, rootCategory));
      const showQrCosmeticOrSunscreen = (isCosmetic(product?.foodlaCategory, rootCategory) ||
      isSunscreen(product?.foodlaCategory, rootCategory));
      const showQrAdditivesInFeedOrFeedLabeling = isFeedCategory(product?.foodlaCategory, rootCategory);
      const showQrMisleadingSalesTerms = isNotStore && (isFeedCategory(product?.foodlaCategory, rootCategory) || isNewFood(product?.foodlaCategory, rootCategory));
      const showQrNutritionClaims = isNotStore && isNewFood(product?.foodlaCategory, rootCategory);
      const showCmrFields = !isFood && isNotStore && (
        isPharma(product?.foodlaCategory, rootCategory) ||
        isCmrHazardCategory(product?.foodlaCategory, rootCategory)
      );
      const showReachSubstance = !isFood && isNotStore && (
        isToy(product?.foodlaCategory, rootCategory) ||
        isElectric(product?.foodlaCategory, rootCategory) ||
        isJewelry(product?.foodlaCategory, rootCategory) ||
        isGoods(product?.foodlaCategory, rootCategory) ||
        isMedical(product?.foodlaCategory, rootCategory) ||
        isChemical(product?.foodlaCategory, rootCategory) ||
        isDetergent(product?.foodlaCategory, rootCategory) ||
        isCosmetic(product?.foodlaCategory, rootCategory) ||
        isSunscreen(product?.foodlaCategory, rootCategory)
      );

      return (
        // plasticTrayPackaging, specifyCEStandards, CE-märkningen
        (
          isGoods(product?.foodlaCategory, rootCategory) ||
          isJewelry(product?.foodlaCategory, rootCategory) ||
          isToy(product?.foodlaCategory, rootCategory) ||
          isElectric(product?.foodlaCategory, rootCategory) ||
          isMedical(product?.foodlaCategory, rootCategory)
        ) || (
          // plasticTrayPackaging, shrinkWrapPackaging, bestBeforeDate
          isNotStore
        ) || (
          // childrenUnder16
          isNotStore &&
          (
            isNewFood(product?.foodlaCategory, rootCategory) ||
            isToy(product?.foodlaCategory, rootCategory) ||
            isElectric(product?.foodlaCategory, rootCategory) ||
            isJewelry(product?.foodlaCategory, rootCategory) ||
            isGoods(product?.foodlaCategory, rootCategory) ||
            isChemical(product?.foodlaCategory, rootCategory) ||
            isDetergent(product?.foodlaCategory, rootCategory) ||
            isCosmetic(product?.foodlaCategory, rootCategory) ||
            isSunscreen(product?.foodlaCategory, rootCategory) ||
            isOTC(product?.foodlaCategory, rootCategory)
          )
        ) || (
          // containsBattery
          isGoods(product?.foodlaCategory, rootCategory) ||
          isJewelry(product?.foodlaCategory, rootCategory) ||
          isToy(product?.foodlaCategory, rootCategory) ||
          isElectric(product?.foodlaCategory, rootCategory) ||
          isMedical(product?.foodlaCategory, rootCategory)
        ) || (
          // compliancePops, containsBannedSubstancesPops
          isGoods(product?.foodlaCategory, rootCategory) ||
          isChemical(product?.foodlaCategory, rootCategory) ||
          isDetergent(product?.foodlaCategory, rootCategory)
        ) || (
          // cmrSubstance, CMRSubstances
          showCmrFields
        ) || (
          // reachSubstance
          showReachSubstance
        ) || (
          // qrRelevantFeedCertifications
          showQualityFields &&
          showQrRelevantFeedCertifications
        ) || (
          // qrFoodHygiene
          showQualityFields &&
          showQrFoodHygiene
        ) || (
          // qrElectronic
          showQualityFields &&
          showQrElectronic
        ) || (
          // qrClassificationLabelingPackaging
          showQualityFields &&
          showQrClassificationLabelingPackaging
        ) || (
          // qrLabelingSwedish, qrMedicalClaims, qrMarketingClaimsCosmetic
          showQualityFields &&
          showQrCosmeticOrSunscreen
        ) || (
          // qrAdditivesInFeed, qrFeedLabeling
          showQualityFields &&
          showQrAdditivesInFeedOrFeedLabeling
        ) || (
          // qrMisleadingSalesTerms
          showQualityFields &&
          showQrMisleadingSalesTerms
        ) || (
          // qrNutritionClaims, qrHealthClaims, qrFoodLabeling
          showQualityFields &&
          showQrNutritionClaims
        ) || (
          // qrEfsaUlValues
          showQualityFields &&
          isNotStore &&
          isFoodSupplements(product?.foodlaCategory, rootCategory)
        ) || (
          // complianceEg12232009, complianceSunscreenRegulation, sunProtectionFactor
          isCosmetic(product?.foodlaCategory, rootCategory) ||
          isSunscreen(product?.foodlaCategory, rootCategory)
        ) || (
          // complianceMetalsSkinContact
          isJewelry(product?.foodlaCategory, rootCategory)
        ) || (
          // confirmGuidanceSwedishElectronics
          isToy(product?.foodlaCategory, rootCategory) ||
          isElectric(product?.foodlaCategory, rootCategory) ||
          isMedical(product?.foodlaCategory, rootCategory)
        ) || (
          // complianceToyDirective
          isToy(product?.foodlaCategory, rootCategory) ||
          isElectric(product?.foodlaCategory, rootCategory)
        ) || (
          // complianceBiocid
          isToy(product?.foodlaCategory, rootCategory) ||
          isChemical(product?.foodlaCategory, rootCategory) ||
          isDetergent(product?.foodlaCategory, rootCategory)
        ) || (
          // complianceBiocidalTreated
          isToy(product?.foodlaCategory, rootCategory) ||
          isElectric(product?.foodlaCategory, rootCategory) ||
          isJewelry(product?.foodlaCategory, rootCategory) ||
          isGoods(product?.foodlaCategory, rootCategory)
        ) || (
          // regulatedSubstance
          product?.reachSubstancesAnnex19072006 &&
          (isToy(product?.foodlaCategory, rootCategory) ||
            isChemical(product?.foodlaCategory, rootCategory) ||
            isDetergent(product?.foodlaCategory, rootCategory))
        )
      );
    }
  },
  {
    field: "supplierArticleNo",
    changeFields: ['purchasingDataSupplierArticleNo'],
    conditionFunc: (product, rootCategory, option) =>
      !option?.isStoreProducer,
  },
  {
    field: "recognizeImages",
    conditionFunc: (product, rootCategory, option) =>
      option?.isApoteaProducer,
  },
];

const APPROVE_FIELDS_FOOD: TApproveFieldConstant[] = [
  {
    field: 'alcoholPercentage',
    conditionFunc: (product, rootCategory) => {
      return isBeverages(product.foodlaCategory, rootCategory);
    },
  },
  {
    field: 'recyclingType',
    conditionFunc: (product, rootCategory) => {
      return isBeverages(product.foodlaCategory, rootCategory);
    },
  },
  {
    field: 'recycling_amount',
    conditionFunc: (product, rootCategory) => {
      return isBeverages(product.foodlaCategory, rootCategory);
    },
  },
  {
    field: 'classification',
    conditionFunc: (product, rootCategory) => {
      return isFruktOrGreen(product.foodlaCategory, rootCategory);
    },
  },
  {
    field: 'country_of_manufacturing',
    conditionFunc: (product, rootCategory) => {
      return !isEgg(product.foodlaCategory, rootCategory);
    },
  },
  {
    field: 'dosageAndUsageInstructions',
    conditionFunc: (product, rootCategory) => {
      return (
        (isSpecialFoodNutrition(product.foodlaCategory, rootCategory) ||
          isFoodSupplements(product.foodlaCategory, rootCategory)) &&
        !isFeedCategory(product.foodlaCategory, rootCategory)
      );
    },
  },
  {
    field: 'preparedForm',
    conditionFunc: (product, rootCategory) => {
      return (
        isSpecialFoodNutrition(product.foodlaCategory, rootCategory) ||
        isFoodSupplements(product.foodlaCategory, rootCategory)
      );
    },
  },
  {
    field: 'eggWeightClass',
    conditionFunc: (product, rootCategory) => {
      return isEgg(product.foodlaCategory, rootCategory);
    },
  },
  {
    field: 'eggPackerApprovalNumber',
    conditionFunc: (product, rootCategory) => {
      return isEgg(product.foodlaCategory, rootCategory);
    },
  },
  {
    field: 'fishProductionMethod',
    conditionFunc: (product, rootCategory) => {
      return isFish(product.foodlaCategory, rootCategory, product);
    },
  },
  {
    field: 'fishCatchMethod',
    conditionFunc: (product, rootCategory) => {
      return isFish(product.foodlaCategory, rootCategory, product);
    },
  },
  {
    field: 'catchArea',
    conditionFunc: (product, rootCategory) => {
      return isFish(product.foodlaCategory, rootCategory, product);
    },
  },
  {
    field: 'brand_food',
    conditionFunc: () => true,
  },
  {
    field: 'ingredient_statement',
    conditionFunc: () => true,
  },
  {
    field: 'nutrition',
    conditionFunc: () => true,
  },
  {
    field: 'vitamins_minerals',
    conditionFunc: () => true,
  },
  {
    field: 'place_of_item_activity',
    conditionFunc: () => true,
  },
  ...APPROVE_FIELDS_ALL,
];

const signalFieldsConditionFunc = (product: IProduct, rootCategory?: IFoodlaCategoryOption) => {
  return (
    ((isPharma(product?.foodlaCategory, rootCategory) || isCmrHazardCategory(product?.foodlaCategory, rootCategory)) &&
      !(isChemical(product?.foodlaCategory, rootCategory) || isToy(product?.foodlaCategory, rootCategory))) ||
    ((isToy(product?.foodlaCategory, rootCategory) || isChemical(product?.foodlaCategory, rootCategory)) &&
      ((product?.signalWords as string) === 'WARNING' || (product?.signalWords as string) === 'DANGER'))
  );
};

const APPROVE_FIELDS_NON_FOOD: TApproveFieldConstant[] = [
  {
    field: 'dosageAndUsageInstructions',
    conditionFunc: (product, rootCategory) => {
      return !isFeedCategory(product.foodlaCategory, rootCategory);
    },
  },
  {
    field: 'non_food_ingredients',
    conditionFunc: (product, rootCategory) => {
      return !isFeedCategory(product.foodlaCategory, rootCategory);
    },
  },
  {
    field: 'signalWords',
    changeFields: ["signalWords", "flammableSubtype"],
    conditionFunc: (product, rootCategory) => {
      return !isFeedCategory(product.foodlaCategory, rootCategory);
    },
  },
  {
    field: 'animalFoodIngredients',
    changeFields: ["animalFoodIngredients", "animalFeedType"],
    conditionFunc: (product, rootCategory) => {
      return isFeedCategory(product.foodlaCategory, rootCategory);
    },
  },
  {
    field: 'place_of_item_activity',
    conditionFunc: (product, rootCategory) => {
      return isFeedCategory(product.foodlaCategory, rootCategory);
    },
  },
  {
    field: 'responsibleLabelingApprovalNumber',
    conditionFunc: (product, rootCategory) => {
      return isFeedCategory(product.foodlaCategory, rootCategory);
    },
  },
  {
    field: 'animalCategoriesForFeed',
    conditionFunc: (product, rootCategory) => {
      return isFeedCategory(product.foodlaCategory, rootCategory);
    },
  },
  {
    field: 'feedAdditives',
    conditionFunc: (product, rootCategory) => {
      return isFeedCategory(product.foodlaCategory, rootCategory);
    },
  },
  {
    field: 'analyticalConstituentsFeed',
    conditionFunc: (product, rootCategory) => {
      return isFeedCategory(product.foodlaCategory, rootCategory);
    },
  },
  {
    field: 'feedingInstructions',
    conditionFunc: (product, rootCategory) => {
      return isFeedCategory(product.foodlaCategory, rootCategory);
    },
  },
  {
    field: 'animalAgeGroup',
    conditionFunc: (product, rootCategory) => {
      return isFeedCategory(product.foodlaCategory, rootCategory);
    },
  },
  {
    field: 'preparedForm',
    conditionFunc: (product, rootCategory) => {
      return (
        isPharma(product.foodlaCategory, rootCategory) ||
        isDetergent(product.foodlaCategory, rootCategory) ||
        isComplementarySupplements(product.foodlaCategory, rootCategory)
      );
    },
  },
  {
    field: 'brand',
    conditionFunc: () => true,
  },
  // white-snus
  {
    field: 'whiteSnusNicotinePerPortion',
    conditionFunc: (product, rootCategory) => {
      return isWhiteSnus(product.foodlaCategory, rootCategory);
    },
  },
  {
    field: 'whiteSnusNicotinePerGram',
    conditionFunc: (product, rootCategory) => {
      return isWhiteSnus(product.foodlaCategory, rootCategory);
    },
  },
  {
    field: 'whiteSnusNicotineStrength',
    conditionFunc: (product, rootCategory) => {
      return isWhiteSnus(product.foodlaCategory, rootCategory);
    },
  },
  {
    field: 'whiteSnusFlavor',
    conditionFunc: (product, rootCategory) => {
      return isWhiteSnus(product.foodlaCategory, rootCategory);
    },
  },
  {
    field: 'whiteSnusPortionsPerUnit',
    conditionFunc: (product, rootCategory) => {
      return isWhiteSnus(product.foodlaCategory, rootCategory);
    },
  },
  {
    field: 'whiteSnusWeightPerPortion',
    conditionFunc: (product, rootCategory) => {
      return isWhiteSnus(product.foodlaCategory, rootCategory);
    },
  },
  {
    field: 'whiteSnusFormat',
    conditionFunc: (product, rootCategory) => {
      return isWhiteSnus(product.foodlaCategory, rootCategory);
    },
  },
  {
    field: 'hazardSymbols',
    conditionFunc: signalFieldsConditionFunc,
  },
  {
    field: 'hazardStatements',
    conditionFunc: signalFieldsConditionFunc,
  },
  {
    field: 'securityNotices',
    conditionFunc: signalFieldsConditionFunc,
  },

  // Electric lamp
  {
    field: 'lampEffect',
    conditionFunc: (product, rootCategory) => {
      return isElectricLamp(product.foodlaCategory, rootCategory);
    },
  },
  {
    field: 'lampModel',
    conditionFunc: (product, rootCategory) => {
      return isElectricLamp(product.foodlaCategory, rootCategory);
    },
  },
  {
    field: 'lampEnergyClass',
    conditionFunc: (product, rootCategory) => {
      return isElectricLamp(product.foodlaCategory, rootCategory);
    },
  },
  {
    field: 'lampTechnology',
    conditionFunc: (product, rootCategory) => {
      return isElectricLamp(product.foodlaCategory, rootCategory);
    },
  },
  {
    field: 'lampSocketType',
    conditionFunc: (product, rootCategory) => {
      return isElectricLamp(product.foodlaCategory, rootCategory);
    },
  },
  {
    field: 'lampDimmable',
    conditionFunc: (product, rootCategory) => {
      return isElectricLamp(product.foodlaCategory, rootCategory);
    },
  },
  {
    field: 'lampEquivalentBulb',
    conditionFunc: (product, rootCategory) => {
      return isElectricLamp(product.foodlaCategory, rootCategory);
    },
  },
  {
    field: 'lampBrightnessLm',
    conditionFunc: (product, rootCategory) => {
      return isElectricLamp(product.foodlaCategory, rootCategory);
    },
  },
  {
    field: 'lampColorTemperatureK',
    conditionFunc: (product, rootCategory) => {
      return isElectricLamp(product.foodlaCategory, rootCategory);
    },
  },
  {
    field: 'lampAverageLifespanH',
    conditionFunc: (product, rootCategory) => {
      return isElectricLamp(product.foodlaCategory, rootCategory);
    },
  },
  {
    field: 'lampColorRenderingRa',
    conditionFunc: (product, rootCategory) => {
      return isElectricLamp(product.foodlaCategory, rootCategory);
    },
  },
  {
    field: 'lampEnergyConsumption',
    conditionFunc: (product, rootCategory) => {
      return isElectricLamp(product.foodlaCategory, rootCategory);
    },
  },
  {
    field: 'lampBeamAngleDeg',
    conditionFunc: (product, rootCategory) => {
      return isElectricLamp(product.foodlaCategory, rootCategory);
    },
  },
  {
    field: "additionalCharacteristics",
    conditionFunc: () => true,
  },
  ...APPROVE_FIELDS_ALL,
];

export const isCategoryChanged = (oldProduct: IProduct, product: IProduct) => {
  const { id: oldFoodlaCategoryId } = oldProduct.foodlaCategory || { id: '' };
  const { id: foodlaCategoryId } = product.foodlaCategory || { id: '' };

  return oldFoodlaCategoryId !== foodlaCategoryId;
};

export const isParentCategoryChanged = (
  oldProduct?: IProduct,
  product?: IProduct,
  rootCategory?: IFoodlaCategoryOption
) => {
  if (!rootCategory) return false;
  // both of old and new product do NOT have category -> not changed
  if (!oldProduct?.foodlaCategory && !product?.foodlaCategory) return false;
  // Next, one of them does NOT have category -> difference -> changed
  if (!oldProduct?.foodlaCategory || !product?.foodlaCategory) return true;

  const oldList = getParentCategories(oldProduct.foodlaCategory, rootCategory);
  oldList.reverse();

  const newList = getParentCategories(product.foodlaCategory, rootCategory);
  newList.reverse();

  return oldList[0] !== newList[0] || oldList[1] !== newList[1];
};

export const getNewApproveByCategory = (
  product: IProduct,
  rootCategory?: IFoodlaCategoryOption,
  options?: { oldProduct?: IProduct, ignoreMissingField?: boolean }
) => {
  const { oldProduct } = options || {};
  const isFood = isNewFood(product.foodlaCategory, rootCategory);
  const isNonFood = isNewNonFood(product.foodlaCategory, rootCategory);

  let checkingFields = APPROVE_FIELDS_ALL;
  if (isFood) {
    checkingFields = APPROVE_FIELDS_FOOD;
  }
  if (isNonFood) {
    checkingFields = APPROVE_FIELDS_NON_FOOD;
  }

  const isMultiBrandProducer = !!product?.producerIsMultiBrand;
  const isStoreProducer = !!product?.producerIsStore;
  const isApoteaProducer = product?.producerOrganizations?.includes('apotea');
  const conditionFuncOption = {
    isApoteaProducer,
    isStoreProducer,
    isMultiBrandProducer,
  }

  let oldCheckingFields = APPROVE_FIELDS_ALL;
  if (oldProduct) {
    if (isNewFood(oldProduct.foodlaCategory, rootCategory)) {
      oldCheckingFields = APPROVE_FIELDS_FOOD;
    }
    if (isNewNonFood(oldProduct.foodlaCategory, rootCategory)) {
      oldCheckingFields = APPROVE_FIELDS_NON_FOOD;
    }
  }

  let approve = cloneDeep(product.approve || []);
  approve = approve
    .map(item => ({ ...item, state: null }))
    // Maybe user changed the category between "food" vs "non-food",
    // -> remove fields which are not in [checkingFields]
    .filter(item => !!checkingFields.some(({ field }) => field === item.field));

  checkingFields.forEach(({ field, conditionFunc }) => {
    if (conditionFunc(product, rootCategory, conditionFuncOption)) {
      if (!options?.ignoreMissingField) {
        const exsiting = approve.some(i => i.field === field);

        let wasApplicable = false;
        if (oldProduct) {
          wasApplicable = oldCheckingFields.some(
            (oldField) =>
              oldField.field === field &&
              oldField.conditionFunc?.(oldProduct, rootCategory, conditionFuncOption),
          );
        }

        // add missing fields
        if (!exsiting && !wasApplicable) {
          approve.push({ field, state: null });
        }
      }
    } else {
      // delete unnecessary fields
      approve = approve.filter(i => i.field !== field);
    }
  });

  return approve;
};

export const removeUnnecessaryAprroveFields = (state: IProduct, rootCategory?: IFoodlaCategoryOption) => {
  let approveList = cloneDeep(state.approve || []);
  const approveForCurrentCategory = getNewApproveByCategory(state, rootCategory);
  // remove fields which do not for current category
  // Only check approve fields from "state.approve", which is for current category
  return approveList.filter(item => {
    return approveForCurrentCategory.some(({ field }) => field === item.field);
  });
};

export const Category = ({
  readOnly,
  sortedImages,
  userRole,
  product,
  state,
  setState,
  approve,
  isSuperuser,
  isUserStore,
  rootCategory,
  categories,
  categoryLoading,
  categoryError,
  fieldVisibility,
}: ICategoryProps) => {
  const isCategoryRequired = useIsFieldRequired('foodlaCategory');
  const { categories: icaCategories, rootCategory: rootIcaCategory } = useCategoriesData({ type: 'ica' });
  const { categories: icaOnlineCategories, rootCategory: rootIcaOnlineCategory } = useCategoriesData({
    type: 'ica_online',
  });
  const { categories: coopCategories, rootCategory: rootCoopCategory } = useCategoriesData({ type: 'coop' });
  const { categories: woltOnlineCategories, rootCategory: rootWoltOnlineCategory } = useCategoriesData({
    type: 'wolt_online',
  });
  const { categories: woltCategories, rootCategory: rootWoltCategory } = useCategoriesData({ type: 'wolt' });
  const { categories: aopteaCategories, rootCategory: rootApoteaCategory } = useCategoriesData({ type: 'apotea' });
  const [savedRecyclingAmount] = useState(state.recycling_amount);
  const [productType, setProductType] = useState('');

  const isFood = isNewFood(state.foodlaCategory, rootCategory);

  const rootFoodlaCategory = useMemo(() => {
    const treeData = cloneDeep(rootCategory)?.children?.find(({ id }) => id === productType);

    if (productType === 'food' && treeData?.children?.length) {
      if (!state.producerIsStore && !isUserStore) {
        treeData.children = treeData.children.filter(({ name }) => name !== 'Butikens egna varor');
      }
      const storeGoods = treeData.children.find(({ name }) => name === 'Butikens egna varor');
      if (storeGoods) {
        const rest = treeData.children.filter(({ name }) => name !== 'Butikens egna varor');
        treeData.children = [storeGoods, ...rest];
      }
    }

    return treeData;
  }, [productType, isUserStore, rootCategory, state]);

  const { icaCategory, coopCategory, apoteaCategory } = useMemo(() => {
    if (!state.foodlaCategory) return {};
    const foundCategory = categories.find(({ id }) => id === state.foodlaCategory?.id);
    const foundApoteaCategory = aopteaCategories.find(({ id }) => {
      return id === (state.foodlaCategory?.apoteaCategory || foundCategory?.apoteaCategory);
    });
    return {
      icaCategory: state.foodlaCategory.icaCategory || foundCategory?.icaCategory || '',
      coopCategory: state.foodlaCategory.coopCategory || foundCategory?.coopCategory || '',
      apoteaCategory: foundApoteaCategory?.name || '',
    };
  }, [state, categories, aopteaCategories]);

  const hasApprove = (field: string) => {
    if (!approve) return false;
    if (!state?.approve) return false;
    if (state.approve.length === 0) return false;
    const approveItem = state.approve.find(x => x.field === field);
    if (!approveItem) return false;
    return true;
  };

  const percentageValues = getPercentageValues();

  const handleChange = (
    event: ChangeEvent<{
      name?: string | undefined;
      value: unknown;
    }>
  ) => {
    const stateName = event.target.name;
    let stateValue = event.target.value;

    if (stateName === 'classification') {
      stateValue = stateValue ?? null;
    }
    stateName && setState({ ...state, [stateName]: stateValue });
  };

  const handleDropdownChange = (
    event: ChangeEvent<{
      name?: string | undefined;
      value: unknown;
    }>
  ) => {
    const stateName = event.target.name;
    let stateValue = event.target.value;

    const val = !stateValue || stateValue === '' ? null : stateValue;

    if (!stateName) return;

    const newState = cloneDeep({ ...state, [stateName]: val });

    if (stateName === 'recyclingType') {
      switch (stateValue) {
        case 'RECYCLABLE_PET_3_SEK':
          newState.recycling_amount = 3;
          break;
        case 'RECYCLABLE_PET':
        case 'ALUMINUM_CAN_2_SEK':
        case 'RECYCLABLE_PET_UP_TO_1000_2_SEK':
          newState.recycling_amount = 2;
          break;
        case 'ALUMINUM_CAN':
        case 'RECYCLABLE_PET_UP_TO_1000':
          newState.recycling_amount = 1;
          break;
        default:
          newState.recycling_amount = null as any;
      }
    }
    setState(newState);
  };

  const handleCategoryChange = (foodlaCategory: IFoodlaCategoryOption) => {
    console.log('--- handleCategoryChange called with:', foodlaCategory.name);
    setState(prevState => {
      const newState = { ...prevState, foodlaCategory };
      if (!isSuperuser) return newState;

      const currentApprove = syncCurrentApprove({
        approve: prevState.approve,
        currentApprove: prevState.currentApprove,
      });

      const approve = restoreApprove({
        approve: getNewApproveByCategory(newState, rootCategory, { oldProduct: product }),
        currentApprove,
      });

      return { ...newState, approve, currentApprove };
    });
  };

  // detect product type
  useEffect(() => {
    if (!rootCategory?.children?.length) return;
    let newProductType = rootCategory.children[0].id;
    rootCategory?.children?.forEach(productCategory => {
      if (!state.foodlaCategory?.id) return;
      if (findCategory(productCategory?.children || [], state.foodlaCategory.id)) {
        newProductType = productCategory.id;
      }
    });
    setProductType(newProductType);
  }, [state.foodlaCategory, rootCategory]);

  return (
    <>
      {/* FC-1950: the id wraps the label and the selectors so an error label can scroll to and focus the choice. */}
      <div id="foodlaCategory">
        <DetailedInputLabel
          heading="Kategori"
          required={isCategoryRequired}
          text="Hör av dig till support@sygrid.com om du är osäker på vilken kategori du ska välja så hjälper vi dig!"
          // link="https://help.foodla.nu/knowledge/vilken-kategori-ska-jag-v%C3%A4lja-i-foodla"
        />
        <FormControl disabled={readOnly} component="fieldset">
          <RadioGroup row value={productType} onChange={event => setProductType(event.target.value)}>
            {rootCategory?.children?.map(productCategory => (
              <FormControlLabel
                disabled={readOnly}
                key={productCategory.id}
                value={productCategory.id}
                control={<Radio color="primary" />}
                label={productCategory.name}
              />
            ))}
          </RadioGroup>
        </FormControl>
        <div style={{ display: 'flex', flexDirection: 'row', flex: 1 }}>
          <CategoryTree
            readOnly={readOnly}
            type="foodla"
            loading={!!categoryLoading}
            error={categoryError}
            selectedCategory={state.foodlaCategory}
            hideLabel={readOnly}
            showFullCategory={readOnly}
            rootCategory={rootFoodlaCategory}
            onSelect={category => {
              const { children, ...data } = category;
              handleCategoryChange(data);
            }}
          />
          {hasApprove('foodlaCategory') && (
            <ThreeWaySwitch
              readOnly={readOnly}
              state={state}
              style={{ flex: 0.1, maxHeight: 56 }}
              setState={setState}
              name="foodlaCategory"
            />
          )}
        </div>
        <MissingFieldHint field="foodlaCategory" />
      </div>

      {isSuperuser && (
        <div style={{ marginTop: 8 }}>
          <OrgCategoryDisplay
            label="ICA kategori:"
            value={icaCategory}
            strikethrough={isCategoryOverridden(state, 'ica')}
            dim={isCategoryDimmed(state, 'ica')}
          />
          <OrgCategory
            readOnly={readOnly}
            label="ICA kategori (manuell):"
            loading={!!categoryLoading}
            autoFill
            isFood={state.foodlaCategory ? isFood : productType === 'food'}
            type="ica"
            categories={icaCategories}
            rootCategory={rootIcaCategory}
            sortedImages={sortedImages}
            state={state}
            setState={setState}
            dim={isCategoryDimmed(state, 'ica')}
          />
          <VSpace h={'5px'} />
          <OrgCategory
            readOnly={readOnly}
            label="ICA Online kategori (manuell):"
            loading={!!categoryLoading}
            autoFill
            isFood={state.foodlaCategory ? isFood : productType === 'food'}
            type="ica_online"
            categories={icaOnlineCategories}
            rootCategory={rootIcaOnlineCategory}
            sortedImages={sortedImages}
            state={state}
            setState={setState}
            dim={isCategoryDimmed(state, 'ica')}
          />
          <VSpace h={'5px'} />
          <OrgCategoryDisplay
            label="Coop kategori:"
            value={coopCategory}
            strikethrough={isCategoryOverridden(state, 'coop')}
            dim={isCategoryDimmed(state, 'coop')}
          />
          <OrgCategory
            readOnly={readOnly}
            label="Coop kategori (manuell):"
            loading={!!categoryLoading}
            type="coop"
            categories={coopCategories}
            rootCategory={rootCoopCategory}
            state={state}
            setState={setState}
            dim={isCategoryDimmed(state, 'coop')}
          />
          <OrgCategory
            readOnly={readOnly}
            label="Wolt online (manuell):"
            loading={!!categoryLoading}
            type="wolt_online"
            categories={woltOnlineCategories}
            rootCategory={rootWoltOnlineCategory}
            state={state}
            setState={setState}
            dim={isCategoryDimmed(state, 'wolt')}
          />
          <OrgCategory
            readOnly={readOnly}
            label="Wolt intern (manuell):"
            loading={!!categoryLoading}
            type="wolt"
            categories={woltCategories}
            rootCategory={rootWoltCategory}
            state={state}
            setState={setState}
            dim={isCategoryDimmed(state, 'wolt')}
          />
          <OrgCategoryDisplay
            label="Apotea kategori:"
            value={apoteaCategory}
            strikethrough={isCategoryOverridden(state, 'apotea')}
            dim={isCategoryDimmed(state, 'apotea')}
          />
          <OrgCategory
            readOnly={readOnly}
            label="Apotea kategori (manuell):"
            loading={!!categoryLoading}
            autoFill={!isCategoryDimmed(state, 'apotea')}
            isFood={state.foodlaCategory ? isFood : productType === 'food'}
            type="apotea"
            categories={aopteaCategories}
            rootCategory={rootApoteaCategory}
            state={state}
            setState={setState}
            dim={isCategoryDimmed(state, 'apotea')}
          />
        </div>
      )}

      {fieldVisibility.eggWeightClass && (
        <>
          <InputLabel
            heading="Viktklass (ägg)"
            text="Ägg av klass A bör klassificeras efter vikt. Det finns mer information i förordningen (EG) 1234/2007 om handelsnormer för ägg."
            link="https://www.svenskaagg.se/wp-content/uploads/2022/07/1960.pdf"
          />
          <div style={{ display: 'flex', flexDirection: 'row', flex: 1, marginTop: 10 }}>
            <ListDropDown
              readOnly={readOnly}
              disableLabel={true}
              listData={eggWeightClass}
              value={state?.eggWeightClass}
              targetName="eggWeightClass"
              handleChange={handleDropdownChange}
              displayEmpty={true}
              style={{ flex: 0.9 }}
            />
            {hasApprove('eggWeightClass') && (
              <ThreeWaySwitch
                readOnly={readOnly}
                state={state}
                style={{ flex: 0.1, maxHeight: 56 }}
                setState={setState}
                name="eggWeightClass"
              />
            )}
          </div>
        </>
      )}
      {fieldVisibility.classification && (
        <div style={{ display: 'flex', flexDirection: 'row', flex: 1, marginTop: 10 }}>
          <ListDropDown
            readOnly={readOnly}
            label="Klassificering"
            displayEmpty
            listData={map(c => ({ value: c }), PRODUCT_CLASSIFICATIONS)}
            handleChange={handleChange}
            value={state.classification}
            targetName="classification"
            style={{ marginBottom: 15, flex: 0.9 }}
          />
          {hasApprove('classification') && (
            <ThreeWaySwitch
              readOnly={readOnly}
              state={state}
              style={{ flex: 0.1, maxHeight: 56 }}
              setState={setState}
              name="classification"
            />
          )}
        </div>
      )}
      {fieldVisibility.alcoholPercentage && (
        <div style={{ display: 'flex', flexDirection: 'row', flex: 1, marginTop: 10 }}>
          <ListDropDown
            readOnly={readOnly}
            label="Alkoholhalt"
            listData={map(c => ({ value: Number(c) }), percentageValues)}
            handleChange={handleChange}
            value={state.alcoholPercentage}
            targetName="alcoholPercentage"
            style={{ flex: 0.9 }}
          />
          {hasApprove('alcoholPercentage') && (
            <ThreeWaySwitch
              readOnly={readOnly}
              state={state}
              style={{ flex: 0.1, maxHeight: 56 }}
              setState={setState}
              name="alcoholPercentage"
            />
          )}
        </div>
      )}
      {fieldVisibility.recyclingType && (
        <>
          <InputLabel heading="Panttyp" />
          <div style={{ display: 'flex', flexDirection: 'row', flex: 1, marginTop: 10 }}>
            <ListDropDown
              readOnly={readOnly}
              disableLabel={true}
              listData={recyclingType}
              value={state?.recyclingType}
              targetName="recyclingType"
              handleChange={handleDropdownChange}
              displayEmpty={true}
              style={{ flexGrow: 1, overflow: 'hidden', paddingTop: 5 }}
            />
            {hasApprove('recyclingType') && (
              <ThreeWaySwitch
                readOnly={readOnly}
                state={state}
                setState={setState}
                name="recyclingType"
                style={{ marginTop: 5, maxHeight: 56 }}
              />
            )}
          </div>
        </>
      )}
      {fieldVisibility.recycling_amount && (
        <>
          <InputLabel heading="Pantbelopp" />
          <div style={{ display: 'flex', flexDirection: 'row', flex: 1, marginTop: 10 }}>
            <ListDropDown
              readOnly={readOnly}
              fullWidth
              displayEmpty
              listData={RECYCLING_AMOUNT}
              handleChange={handleChange}
              value={state.recycling_amount}
              targetName="recycling_amount"
              style={{ flexGrow: 1, overflow: 'hidden', paddingTop: 5 }}
            />
            {hasApprove('recycling_amount') && (
              <ThreeWaySwitch
                readOnly={readOnly}
                state={state}
                setState={setState}
                name="recycling_amount"
                style={{ marginTop: 5, maxHeight: 56 }}
              />
            )}
          </div>
        </>
      )}
    </>
  );
};

export default Category;
